import ErrorUi from 'client/components/core/Error/ui/ErrorUi'
import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useEffect, useState } from 'react'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { createWebsitePage } from 'src/editor/api/website-pages-api'
import { DOMAIN_PLACEHOLDER } from 'src/editor/shared/constants/website-consts'
import { CheckIcon } from 'src/editor/shared/icons/check-icon'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ModalWindow } from 'src/editor/shared/ui-lib/modal-window'
import { ScrollImage } from 'src/editor/shared/ui-lib/scroll-image'
import { generatePathFromText } from 'src/editor/shared/utils/generate-path-from-text'
import { sanitizeUrlInput } from 'src/editor/shared/utils/sanitize-url-input'
import { getWebsiteDomain } from 'src/editor/store/website-settings/website-settings-selector'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'

export interface AddBlogPageModalProps {
  isVisible: boolean
  websiteId: number
  currentWebsiteLocale: PageLocaleEnum
  onClose?: () => void
}

export const CreateBlogModal = ({
  isVisible,
  websiteId,
  currentWebsiteLocale,
  onClose,
}: AddBlogPageModalProps) => {
  const { t } = useTypedTranslation()
  const websiteDomain = useWebsiteSettings(getWebsiteDomain)

  const [name, setName] = React.useState('')
  const [url, setUrl] = React.useState('')
  // TODO: replace with real data when API is ready

  const blogPageTemplates = [
    {
      id: 1,
      src: 'https://d1yei2z3i6k35z.cloudfront.net/4225238/691b3bd22ee72_Careercouching.jpg',
    },
    {
      id: 2,
      src: 'https://d1yei2z3i6k35z.cloudfront.net/4225238/691b3bd22ee72_Careercouching.jpg',
    },
    {
      id: 3,
      src: 'https://d1yei2z3i6k35z.cloudfront.net/4225238/691b3bd22ee72_Careercouching.jpg',
    },
  ]

  const [selectedTemplate, setSelectedTemplate] = useState(
    blogPageTemplates[0].id,
  )
  const [isSubmitAttempted, setIsSubmitAttempted] = React.useState(false)

  const resetForm = React.useCallback(() => {
    setName('')
    setUrl('')
    setSelectedTemplate(blogPageTemplates[0].id)
    setIsSubmitAttempted(false)
  }, [])

  useEffect(() => {
    if (isVisible) {
      resetForm()
    }
  }, [isVisible, resetForm])

  // const { fetcher, isLoading } = useMutationWithValidation({
  //   requestFunction: createWebsiteBlog,
  //   successToastMessage: t(
  //     'website_settings.sidebar.page_settings.created_successfully',
  //   ),
  //   handleSuccess: () => {
  //     resetForm()
  //     onClose?.()
  //   },
  //   invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
  //   retry: false,
  // })

  const nameError =
    isSubmitAttempted && !name.trim()
      ? t('validation.errors.form_field_should_not_be_empty')
      : undefined

  const handleCreateBlogPage = () => {
    setIsSubmitAttempted(true)

    if (!name.trim() || !selectedTemplate) {
      return
    }

    // fetcher({
    //   websiteId,
    //   name: name.trim(),
    //   url,
    //   locale: currentWebsiteLocale,
    // })
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(
      `https://${websiteDomain?.name || DOMAIN_PLACEHOLDER}/${url}`,
    )
  }

  const handleFocusUrlInput = () => {
    if (!url) {
      setUrl(generatePathFromText(name))
    }
  }

  return (
    <ModalWindow
      isOpened={isVisible}
      onClose={onClose}
      title={t('pages_management.create_blog_page_title')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('global.cancel'),
      }}
      confirmButtonProps={{
        onClick: handleCreateBlogPage,
        isLoading: false, // TODO: replace with real loading state when API is ready
        children: t('core.header.save'),
      }}
    >
      <div className="flex flex-col gap-1">
        <TextInputControl
          wrapperProps={{
            className: 'mt-1 mb-2',
            labelText: t('pages_management.create_page_name_label'),
            required: true,
            skipTranslation: true,
          }}
          className={twMerge(nameError && 'border-error-500')}
          id="blog-page-name"
          placeholder={t('pages_management.create_blog_page_name_placeholder')}
          value={name}
          update={value => setName(value)}
          error={nameError}
        />
        <TextInputControl
          wrapperProps={{
            className: 'mt-1 mb-2 w-full',
            labelText: t('pages_management.create_page_url_label'),
            skipTranslation: true,
          }}
          appendix={`https://${websiteDomain?.name || DOMAIN_PLACEHOLDER}/`}
          id="blog-page-url"
          value={url}
          update={value => setUrl(sanitizeUrlInput(value))}
          onCopy={handleCopyUrl}
          onFocus={handleFocusUrlInput}
        />
        <div className="mt-1 mb-0">
          <div className="max-h-80 overflow-y-auto">
            <div className="grid w-full grid-cols-1 justify-items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {blogPageTemplates.map(({ id, src }) => {
                const isSelected = selectedTemplate === id

                return (
                  <button
                    type="button"
                    onClick={() => setSelectedTemplate(id)}
                    className={twMerge(
                      'relative h-28 w-48 cursor-pointer overflow-hidden rounded-lg border transition-colors hover:border-success-300',
                      isSelected ? 'border-success-300' : 'border-border200',
                    )}
                  >
                    <div className="absolute top-1.5 right-1.5 z-10">
                      {isSelected ? (
                        <div className="flex items-center justify-center rounded-full bg-success-400">
                          <CheckIcon
                            width={16}
                            height={16}
                            className="fill-white"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center rounded-full bg-iconDisabled hover:bg-success-400">
                          <CheckIcon
                            width={16}
                            height={16}
                            className="fill-white"
                          />
                        </div>
                      )}
                    </div>
                    <div className="h-full w-full overflow-hidden rounded-md bg-bgSecondary">
                      <ScrollImage
                        src={src}
                        containerProps={{ className: 'h-full w-full' }}
                        className="h-auto w-full"
                      />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </ModalWindow>
  )
}
